import { RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { prefersReducedMotion, useInView } from '../hooks/use-reveal';
import { simulateDeadline, type DemoDay } from '../lib/deadline-demo';

import { SectionLabel } from './section-label';
import '../styles/deadline.css';

const scenarios = [
  { id: 'recesso', label: 'Recesso e Carnaval', act: 'Contestação', start: '2026-12-16', days: 15 },
  {
    id: 'embargos',
    label: 'Embargos no recesso',
    act: 'Embargos de declaração',
    start: '2026-12-16',
    days: 5,
  },
  { id: 'outubro', label: 'Feriado de outubro', act: 'Contestação', start: '2026-10-02', days: 15 },
] as const;

type ScenarioId = (typeof scenarios)[number]['id'];

const rules = [
  ['Art. 224', 'Exclui o dia do começo e inclui o do vencimento.'],
  ['Art. 219', 'Conta apenas os dias úteis.'],
  ['Art. 220', 'Suspende a contagem no recesso forense, de 20/12 a 20/01.'],
  [
    'Feriados',
    'Nacionais e móveis já incluídos. Os locais e as suspensões do tribunal, o escritório cadastra.',
  ],
] as const;

const weekdays = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];
const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** Intervalo entre um dia e o próximo na animação da contagem. */
const STEP_MS = 42;

function formatLong(date: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));
}

function plural(count: number, one: string, many: string) {
  return `${count} ${count === 1 ? one : many}`;
}

function DayCell({ day, revealed }: { day: DemoDay; revealed: boolean }) {
  const showMonth = day.day === 1 || day.kind === 'start';

  return (
    <span
      className="lp-cal-day"
      data-kind={day.kind}
      data-revealed={revealed}
      title={day.label ?? undefined}
    >
      {showMonth && <small>{months[day.month - 1]}</small>}
      {day.day}
      {day.count !== null && day.kind === 'counted' && <b>{day.count}</b>}
      {day.kind === 'due' && <b>vence</b>}
    </span>
  );
}

type Scenario = (typeof scenarios)[number];

interface CalendarRunProps {
  scenario: Scenario;
  /** Só conta com o calendário na tela — quem chega no meio não perde o começo. */
  active: boolean;
  onReplay: () => void;
}

/**
 * Uma contagem do começo ao fim. A seção troca a `key` para recomeçar (outro
 * exemplo, "contar de novo"), e o estado nasce zerado em vez de ser resetado
 * dentro de um efeito.
 */
function CalendarRun({ scenario, active, onReplay }: CalendarRunProps) {
  const simulation = useMemo(
    () => simulateDeadline(scenario.start, scenario.days),
    [scenario.start, scenario.days],
  );
  const startIndex = simulation.days.findIndex((day) => day.kind === 'start');
  const dueIndex = simulation.days.findIndex((day) => day.kind === 'due');
  const [step, setStep] = useState(() => (prefersReducedMotion() ? dueIndex : startIndex));
  const done = step >= dueIndex;

  useEffect(() => {
    if (!active || done) return;

    const timer = window.setInterval(() => {
      setStep((current) => Math.min(current + 1, dueIndex));
    }, STEP_MS);

    return () => window.clearInterval(timer);
  }, [active, done, dueIndex]);

  const counted = simulation.days
    .slice(0, step + 1)
    .reduce((total, day) => (day.count !== null ? Math.max(total, day.count) : total), 0);

  const { recess, weekend, holiday } = simulation.excluded;
  // "Segunda-feira de Carnaval" e "Terça-feira de Carnaval" viram um nome só.
  const holidayNames = [
    ...new Set(
      simulation.holidays.map((label) => (label.includes('Carnaval') ? 'Carnaval' : label)),
    ),
  ];
  const breakdown = [
    recess && plural(recess, 'dia de recesso', 'dias de recesso'),
    weekend && plural(weekend, 'dia de fim de semana', 'dias de fim de semana'),
    holiday && `${plural(holiday, 'feriado', 'feriados')} (${holidayNames.join(', ')})`,
  ].filter(Boolean);

  return (
    <>
      <div className="lp-cal-status">
        <div>
          <small>{scenario.act}</small>
          <strong>
            Publicada em {formatLong(scenario.start).replace(/^\S+, /, '')} · {scenario.days} dias
            úteis
          </strong>
        </div>
        <div className="lp-cal-counter" aria-hidden="true">
          <span>{String(counted).padStart(2, '0')}</span>/{scenario.days}
        </div>
      </div>

      <div className="lp-cal-grid" aria-hidden="true">
        {weekdays.map((weekday) => (
          <span key={weekday} className="lp-cal-weekday">
            {weekday}
          </span>
        ))}
        {simulation.days.map((day, index) => (
          <DayCell key={day.date} day={day} revealed={index <= step} />
        ))}
      </div>

      <div className="lp-cal-legend" aria-hidden="true">
        <span data-kind="counted">Dia útil</span>
        <span data-kind="weekend">Fim de semana</span>
        <span data-kind="holiday">Feriado</span>
        <span data-kind="recess">Recesso</span>
      </div>

      <div className="lp-cal-result" data-done={done} aria-live="polite">
        <div>
          <small>Vencimento</small>
          <strong>{done ? formatLong(simulation.dueDate) : 'Contando…'}</strong>
          {done && breakdown.length > 0 && <p>Não contaram: {breakdown.join(' · ')}.</p>}
        </div>
        <button
          type="button"
          className="lp-cal-replay"
          onClick={onReplay}
          aria-label="Contar de novo"
        >
          <RotateCcw size={16} />
        </button>
      </div>
    </>
  );
}

export function DeadlineSection() {
  const [scenarioId, setScenarioId] = useState<ScenarioId>('recesso');
  const [run, setRun] = useState(0);
  const card = useRef<HTMLDivElement>(null);
  const inView = useInView(card, '0px 0px -20% 0px');

  const scenario = scenarios.find((item) => item.id === scenarioId) ?? scenarios[0];
  const replay = () => setRun((value) => value + 1);

  return (
    <section
      id="prazos"
      aria-labelledby="lp-deadline-title"
      className="bg-(--lp-soft) scroll-mt-20 py-24 sm:py-32"
    >
      <div className="mx-auto grid max-w-[1180px] items-start gap-12 px-5 sm:px-6 lg:grid-cols-[5fr_6fr] lg:gap-16">
        <div className="lg:sticky lg:top-28" data-reveal>
          <SectionLabel>Prazos</SectionLabel>
          <h2
            id="lp-deadline-title"
            className="text-(--lp-mute) mt-5 text-balance text-[clamp(32px,4.2vw,48px)] font-medium leading-[1.08] tracking-[-0.04em]"
          >
            Prazo contado pelo CPC. <span className="text-(--lp-ink)">Com a conta à vista.</span>
          </h2>
          <p className="text-(--lp-body) mt-5 text-[17px] leading-relaxed">
            Um dia a menos pode custar o direito do cliente. O Sancus conta em dias úteis e mostra
            cada dia que não contou, e por quê. Escolha um exemplo e veja a contagem.
          </p>
          <dl className="divide-(--lp-line) border-(--lp-line) mt-8 divide-y border-y">
            {rules.map(([article, effect]) => (
              <div key={article} className="grid grid-cols-[92px_1fr] gap-4 py-3.5">
                <dt className="text-(--lp-ink) text-[14px] font-medium">{article}</dt>
                <dd className="text-(--lp-body) text-[14px] leading-relaxed">{effect}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div ref={card} className="lp-cal">
          <div className="lp-cal-scenarios" role="group" aria-label="Escolher um exemplo de prazo">
            {scenarios.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={item.id === scenarioId}
                onClick={() => {
                  setScenarioId(item.id);
                  replay();
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          <CalendarRun
            key={`${scenarioId}-${run}`}
            scenario={scenario}
            active={inView}
            onReplay={replay}
          />

          <p className="lp-cal-note">
            Simulação com o calendário nacional. No Sancus, a conta considera também os feriados
            locais e as suspensões do seu tribunal.
          </p>
        </div>
      </div>
    </section>
  );
}
